import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import FormInput from '@/components/forms/FormInput';
import { GameRegistry, GameType, LOBBY_ICON_LABELS, LobbyIcon, PokerVariant, TournamentType } from '@repo/common-types';
import { toast } from 'react-toastify';
import { ReactComponent as PeopleIcon } from '@/assets/icons/people.svg';
import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';
import usePayoutStructures from '@/hooks/usePayoutStructures';
import useBlindStructures from '@/hooks/useBlindStructures';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormStatusReducer } from '@/hooks/useFormStatusReducer';
import useGetPlayerTiers from '@/hooks/useGetPlayerTiers';

// Local enum constants for tournament customization
const TournamentIcon = {
  NONE: 'NONE',
  PHNM_LOGO: 'PHNM_LOGO',
  STAR_LOGO: 'STAR_LOGO',
  BOUNTY_LOGO: 'BOUNTY_LOGO',
  DIAMOND_LOGO: 'DIAMOND_LOGO',
  TICKET_LOGO: 'TICKET_LOGO',
  FREE_LOGO: 'FREE_LOGO',
} as const;

const TournamentColor = {
  DEFAULT_WHITE: 'DEFAULT_WHITE',
  PURPLE: 'PURPLE',
  PINK: 'PINK',
  LIGHT_BLUE: 'LIGHT_BLUE',
  LIGHT_GREEN: 'LIGHT_GREEN',
  ORANGE: 'ORANGE',
  RED: 'RED',
  YELLOW: 'YELLOW',
  WATERMELON: 'WATERMELON',
  LEMON: 'LEMON',
} as const;

const getValidationSchema = ({ blindStructures, payoutStructures }) => {
  return Yup.object().shape({
    name: Yup.string().required('Name is required'),
    type: Yup.string().required('Type is required'),
    game: Yup.string().required('Game is required'),
    minPlayers: Yup.number()
      .required('Minimum players is required')
      .min(1, 'Minimum players must be greater than 1')
      .test(
        'min-players-must-be-less-than-max-players',
        'Minimum players must be less than maximum players',
        (value, context) => {
          return value < context?.parent?.maxPlayers;
        },
      ),
    maxPlayers: Yup.number()
      .required('Maximum players is required')
      .min(1, 'Maximum players must be greater than 1')
      .test(
        'max-players-must-be-greater-than-min-players',
        'Maximum players must be greater than minimum players',
        (value, context) => {
          return value > context?.parent?.minPlayers;
        },
      ),
    buyIn: Yup.number().required('Buy in is required').min(0, 'Buy in must be greater than 0'),
    guaranteedPrizePool: Yup.number()
      .required('Guaranteed prize pool is required')
      .min(0, 'Guaranteed prize pool must be greater than 0'),
    registrationOpenPeriodInMinutes: Yup.number()
      .required('Registration open period is required')
      .default(120)
      .min(0, 'Registration open period must be greater than 0'),
    lateRegistrationUpToLevel: Yup.number().min(0, 'Late registration period must be greater than 0'),
    rake: Yup.number().required('Rake is required').min(0, 'Rake must be greater than 0'),
    payoutStructureId: Yup.string()
      .required('Payout structure is required')
      .test(
        'payout-structure-must-handle-max-players',
        'Payout structure must have enough payouts to handle the player count',
        (value, context) => {
          const selectedPayoutStructure = payoutStructures?.find((structure) => structure.id === value);
          const largestPayoutBracket = selectedPayoutStructure?.brackets?.reduce((acc, curr) => {
            if (curr.playersTo > acc) {
              acc = curr.playersTo;
            }
            return acc;
          }, 0);

          return !(largestPayoutBracket && largestPayoutBracket < context?.parent?.maxPlayers);
        },
      ),
    tableSize: Yup.number().required('Table size is required').min(1, 'Table size must be greater than 1'),
    startingChips: Yup.number().required('Starting chips is required').min(0, 'Starting chips must be greater than 0'),
    blindIntervalInMinutes: Yup.number()
      .required('Blind interval is required')
      .min(0, 'Blind interval must be greater than 0'),
    blindStructureId: Yup.string()
      .required('Blind structure is required')
      .test(
        'blind-structure-has-bring-ins-if-stud-or-mixed',
        'Stud and mixed games require a blind structure with bring-ins',
        (value, context) => {
          if (context?.parent?.game && value) {
            const currentBlindStructure = blindStructures?.find((structure) => structure.id === value);
            const { stud, mixed } = checkVariants([context?.parent?.game as PokerVariant]);
            if (stud || mixed) {
              const validBlindStructureForVariant = currentBlindStructure?.levels?.every(
                (level) => !!level.bringIn && level.bringIn > 0,
              );
              if (!validBlindStructureForVariant) {
                return false;
              }
            }
          }
          return true;
        },
      ),
    maxAddons: Yup.number().required('Max addons is required').min(0, 'Max addons must be greater or equal than 0'),
    maxReentries: Yup.number()
      .required('Max reentries is required')
      .min(0, 'Max reentries must be greater or equal than 0'),
    bountyAmount: Yup.number()
      .required('Bounty amount is required')
      .min(0, 'Bounty amount must be greater or equal than 0'),
    minimumTierRequired: Yup.string().nullable().optional(),
    // Timebank validation fields
    startingTimebankInSeconds: Yup.number()
      .required('Starting timebank is required')
      .min(10, 'Starting timebank must be at least 10 seconds')
      .max(300, 'Starting timebank cannot exceed 300 seconds (5 minutes)'),
    timebankRefillAmountInSeconds: Yup.number()
      .required('Timebank refill amount is required')
      .min(10, 'Timebank refill amount must be at least 10 seconds')
      .max(120, 'Timebank refill amount cannot exceed 120 seconds'),
    timebankRefillEveryLevels: Yup.number()
      .required('Timebank refill frequency is required')
      .min(0, 'Timebank refill frequency must be 0 or greater (0 = disabled)')
      .max(50, 'Timebank refill frequency cannot exceed 50 levels'),
    startingDisconnectionTimebankInSeconds: Yup.number()
      .required('Starting disconnection timebank is required')
      .min(10, 'Starting disconnection timebank must be at least 10 seconds')
      .max(300, 'Starting disconnection timebank cannot exceed 300 seconds (5 minutes)'),
    icon: Yup.mixed<string>()
      .oneOf(Object.values(TournamentIcon) as string[])
      .nullable()
      .default(TournamentIcon.NONE),
    titleColor: Yup.string()
      .oneOf(Object.values(TournamentColor) as string[])
      .default(TournamentColor.DEFAULT_WHITE)
      .nullable(),
    lobbyIcons: Yup.array()
      .of(Yup.string().oneOf(Object.values(LobbyIcon) as string[]))
      .default([])
      .ensure(),
  });
};

const TournamentTemplateModalForm = ({
  onClose,
  selectedTemplateId,
  viewOnly = false,
}: {
  onClose: () => void;
  selectedTemplateId: string | null;
  viewOnly?: boolean;
}) => {
  const { playerTiers } = useGetPlayerTiers();
  const { payoutStructures } = usePayoutStructures();
  const { blindStructures } = useBlindStructures();
  const { tournamentTemplates, createTournamentTemplate, updateTournamentTemplate } = useTournamentTemplates();

  const [formStatus, dispatchFormStatus] = useFormStatusReducer();
  const [selectedLobbyIcons, setSelectedLobbyIcons] = useState<LobbyIcon[]>([]);

  const selectedTemplate = tournamentTemplates?.find((template) => template.id === selectedTemplateId);

  const form = useForm({
    resolver: yupResolver(
      getValidationSchema({
        blindStructures,
        payoutStructures,
      }),
    ),
    defaultValues: selectedTemplate || {
      type: TournamentType.STANDARD,
      // Timebank defaults
      startingTimebankInSeconds: 15,
      timebankRefillAmountInSeconds: 15,
      timebankRefillEveryLevels: 10,
      startingDisconnectionTimebankInSeconds: 30,
      guaranteedPrizePool: 0,
      registrationOpenPeriodInMinutes: 120,
      // Tournament customization defaults
      icon: TournamentIcon.NONE,
      titleColor: TournamentColor.DEFAULT_WHITE,
      lobbyIcons: [],
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (selectedTemplate) {
      form.reset(selectedTemplate);
      setSelectedLobbyIcons(((selectedTemplate as any).lobbyIcons || []) as LobbyIcon[]);
    }
  }, [selectedTemplate, form]);

  const {
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
    control,
    setValue,
  } = form;

  // Check if lobby icons have changed
  const lobbyIconsChanged = useMemo(() => {
    const originalIcons = ((selectedTemplate as any)?.lobbyIcons || []) as LobbyIcon[];
    const a = [...selectedLobbyIcons].sort();
    const b = [...originalIcons].sort();
    return JSON.stringify(a) !== JSON.stringify(b);
  }, [selectedLobbyIcons, selectedTemplate]);

  // Combined dirty state including lobby icons
  const hasChanges = isDirty || lobbyIconsChanged;

  // State for the admin interface (total buy-in and rake amount)
  const [adminTotalBuyIn, setAdminTotalBuyIn] = React.useState(0);
  const [adminRakeAmount, setAdminRakeAmount] = React.useState(0);

  // Initialize admin values from existing template
  useEffect(() => {
    if (selectedTemplate) {
      setAdminTotalBuyIn(selectedTemplate.totalBuyin);
      setAdminRakeAmount(selectedTemplate.rake);
    }
  }, [selectedTemplate]);

  // Calculate and update form values when admin inputs change
  useEffect(() => {
    const prizePoolBuyIn = Math.max(0, adminTotalBuyIn - adminRakeAmount);
    setValue('buyIn', prizePoolBuyIn, { shouldDirty: true, shouldValidate: true });
    setValue('rake', adminRakeAmount, { shouldDirty: true, shouldValidate: true });
  }, [adminTotalBuyIn, adminRakeAmount, setValue]);

  const prizePoolBuyIn = Math.max(0, adminTotalBuyIn - adminRakeAmount);

  const maxPlayers = watch('maxPlayers');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('minPlayers')) {
      form.trigger('minPlayers');
    }
    if (Object.keys(form.formState.dirtyFields).includes('payoutStructureId')) {
      form.trigger('payoutStructureId');
    }
  }, [maxPlayers, form.formState.dirtyFields]);

  const minPlayers = watch('minPlayers');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('maxPlayers')) {
      form.trigger('maxPlayers');
    }
  }, [minPlayers, form.formState.dirtyFields]);

  const game = watch('game');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('blindStructureId')) {
      form.trigger('blindStructureId');
    }
  }, [game, form.formState.dirtyFields]);

  const onSubmit = useCallback(
    (data: any) => {
      if (!data.game) {
        return;
      }

      dispatchFormStatus({ type: 'START' });
      if (selectedTemplate?.id) {
        const updateData = {
          ...data,
          id: selectedTemplate.id,
          minimumTierRequired: data.minimumTierRequired ? data.minimumTierRequired : null,
          lobbyIcons: selectedLobbyIcons,
        };

        updateTournamentTemplate(updateData, {
          onSuccess: () => {
            dispatchFormStatus({ type: 'SUCCESS' });
            onClose();
            toast.success('Tournament template updated successfully');
          },
          onError: (error) => {
            dispatchFormStatus({ type: 'ERROR', error: error?.message || 'Failed to update tournament template' });
            toast.error('Failed to update tournament template');
          },
        });
        return;
      } else {
        const createData = {
          ...data,
          lobbyIcons: selectedLobbyIcons,
        };
        createTournamentTemplate(createData, {
          onSuccess: () => {
            dispatchFormStatus({ type: 'SUCCESS' });
            onClose();
            toast.success('Tournament template created successfully');
          },
          onError: (error) => {
            dispatchFormStatus({ type: 'ERROR', error: error?.message || 'Failed to create tournament template' });
            toast.error('Failed to create tournament template');
          },
        });
      }
    },
    [
      selectedTemplate,
      updateTournamentTemplate,
      createTournamentTemplate,
      onClose,
      dispatchFormStatus,
      selectedLobbyIcons,
    ],
  );

  const handleLobbyIconToggle = (icon: LobbyIcon) => {
    setSelectedLobbyIcons((prev) => (prev.includes(icon) ? prev.filter((i) => i !== icon) : [...prev, icon]));
  };

  const blindStructureId = watch('blindStructureId');
  // set using the blind structure levels
  const lateRegistrationOptions = useMemo(() => {
    if (!blindStructureId) {
      return [];
    }
    const blindStructureLevels = blindStructures?.find((structure) => structure.id === blindStructureId)?.levels;
    if (!blindStructureLevels) {
      return [];
    }

    return blindStructureLevels.map((level) => {
      return {
        value: level.levelNumber,
        label: `Level ${level.levelNumber}`,
      };
    });
  }, [blindStructureId, blindStructures]);

  // Tournament customization options
  const iconOptions = [
    { value: TournamentIcon.NONE, label: 'None' },
    { value: TournamentIcon.PHNM_LOGO, label: 'PHNM Logo' },
    { value: TournamentIcon.STAR_LOGO, label: 'Star Logo' },
    { value: TournamentIcon.BOUNTY_LOGO, label: 'Bounty Logo' },
    { value: TournamentIcon.DIAMOND_LOGO, label: 'Diamond Logo' },
    { value: TournamentIcon.TICKET_LOGO, label: 'Ticket Logo' },
    { value: TournamentIcon.FREE_LOGO, label: 'Free Logo' },
  ];

  const colorOptions = [
    { value: TournamentColor.DEFAULT_WHITE, label: 'Default White' },
    { value: TournamentColor.PURPLE, label: 'Purple' },
    { value: TournamentColor.PINK, label: 'Pink' },
    { value: TournamentColor.LIGHT_BLUE, label: 'Light Blue' },
    { value: TournamentColor.LIGHT_GREEN, label: 'Light Green' },
    { value: TournamentColor.ORANGE, label: 'Orange' },
    { value: TournamentColor.RED, label: 'Red' },
    { value: TournamentColor.YELLOW, label: 'Yellow' },
    { value: TournamentColor.WATERMELON, label: 'Watermelon' },
    { value: TournamentColor.LEMON, label: 'Lemon' },
  ];

  return (
    <FormProvider {...form}>
      <Modal isOpen toggle={onClose} id="create-tournament-template" className="modal-lg" centered>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            {viewOnly ? 'View' : selectedTemplate?.id ? 'Edit' : 'Create New'} Tournament Template
          </ModalHeader>
          <ModalBody style={{ maxHeight: '75vh' }} className="overflow-auto">
            <div className="w-100 d-flex flex-column justify-content-start max-h-[75vh] gap-2 overflow-auto">
              <div className="w-100 d-flex justify-content-between flex-row gap-2 px-2">
                <Col>
                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Template Name" name="name" disabled={viewOnly} />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="game"
                        label="Game Type"
                        type="select"
                        options={Object.values(GameType)
                          .filter((type) => {
                            const gameInfo = GameRegistry[type];
                            return !gameInfo?.capped;
                          })
                          .map((type) => ({ value: type, label: type }))}
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="minPlayers"
                        label="Minimum Players"
                        type="number"
                        Icon={PeopleIcon}
                        disabled={viewOnly}
                        min={2}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="maxPlayers"
                        label="Maximum Players"
                        type="number"
                        Icon={PeopleIcon}
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  {/* Modified Buy-in and Rake Section */}
                  <Row className="w-100">
                    <Col>
                      <label className="form-label">Total Buy-In</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminTotalBuyIn}
                          onChange={(e) => setAdminTotalBuyIn(Number(e.target.value) || 0)}
                          disabled={viewOnly}
                          step="0.01"
                        />
                      </div>
                      {!viewOnly && (
                        <small className="text-muted">Total amount players pay to enter (prize pool + rake)</small>
                      )}
                    </Col>
                    <Col>
                      <label className="form-label">Rake/Fee</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminRakeAmount}
                          onChange={(e) => setAdminRakeAmount(Number(e.target.value) || 0)}
                          disabled={viewOnly}
                          step="0.01"
                        />
                      </div>
                      {!viewOnly && <small className="text-muted">Fee amount deducted from total buy-in</small>}
                    </Col>
                  </Row>
                  {/* Display calculated values */}
                  {(adminTotalBuyIn > 0 || adminRakeAmount > 0) && (
                    <Row className="w-100 my-2">
                      <Col>
                        <div className="bg-light rounded p-2">
                          <small className="text-muted d-block">Tournament Configuration:</small>
                          <strong>
                            ${prizePoolBuyIn.toFixed(2)} + ${adminRakeAmount.toFixed(2)}
                          </strong>
                          <br />
                          <small className="text-muted">
                            Prize Pool: ${prizePoolBuyIn.toFixed(2)} | Rake: ${adminRakeAmount.toFixed(2)} | Rake %:{' '}
                            {((adminRakeAmount / prizePoolBuyIn) * 100).toFixed(2)}
                          </small>
                        </div>
                      </Col>
                    </Row>
                  )}
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="guaranteedPrizePool"
                        label="Guarantee Prize Pool"
                        type="number"
                        Icon={TetherIcon}
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="payoutStructureId"
                        label="Payout Structure"
                        type="select"
                        options={
                          payoutStructures?.map((structure) => ({ value: structure.id, label: structure.name })) ?? []
                        }
                        disabled={viewOnly}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="blindStructureId"
                        label="Blind Structure"
                        type="select"
                        options={
                          blindStructures?.map((structure) => ({ value: structure.id, label: structure.name })) ?? []
                        }
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="registrationOpenPeriodInMinutes"
                        label="Registration Open Time (mins)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="lateRegistrationUpToLevel"
                        label="Late Registration Period (up to level)"
                        type="select"
                        options={lateRegistrationOptions}
                        valueAsNumber
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100 border-top border-secondary border-white py-3 opacity-25" />
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="blindIntervalInMinutes"
                        label="Blind Duration (mins)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  {/* Timebank Configuration Section */}
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="startingTimebankInSeconds"
                        label="Starting Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="15"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingDisconnectionTimebankInSeconds"
                        label="Disconnection Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="30"
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillAmountInSeconds"
                        label="Timebank Refill Amount (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="30"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillEveryLevels"
                        label="Refill Every X Levels (0 = disabled)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={viewOnly}
                        placeholder="10"
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="tableSize"
                        label="How Many Handed"
                        type="select"
                        valueAsNumber
                        options={Array.from({ length: 12 }, (_, i) => ({ value: i + 2, label: `${i + 2} Handed` }))}
                        disabled={viewOnly}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingChips"
                        label="Starting Chips Stack"
                        type="number"
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="maxAddons"
                        label="Max Addons"
                        type="number"
                        allowDecimal={false}
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="maxReentries"
                        label="Max Reentries"
                        type="select"
                        includeEmptyOption={false}
                        options={[
                          {
                            value: 0,
                            label: '0',
                          },
                          {
                            value: 1,
                            label: '1',
                          },
                          {
                            value: 2,
                            label: '2',
                          },
                          {
                            value: 3,
                            label: '3',
                          },
                          {
                            value: 5,
                            label: '5',
                          },
                          {
                            value: 10,
                            label: '10',
                          },
                          {
                            value: 9999,
                            label: 'Unlimited',
                          },
                        ]}
                        disabled={viewOnly}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="bountyAmount"
                        label="Bounty Amount"
                        type="number"
                        disabled={viewOnly}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="minimumTierRequired"
                        label="Minimum Tier Required"
                        type="select"
                        options={playerTiers?.map((tier) => ({
                          value: tier.id,
                          label: tier.name,
                        }))}
                      />
                    </Col>
                    <Col />
                  </Row>

                  {/* Tournament Customization Section */}
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="icon"
                        label="Tournament Icon"
                        type="select"
                        options={iconOptions}
                        disabled={viewOnly}
                        includeEmptyOption={false}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="titleColor"
                        label="Title Color"
                        type="select"
                        options={colorOptions}
                        disabled={viewOnly}
                        includeEmptyOption={false}
                      />
                    </Col>
                  </Row>

                  {/* Tournament Features Section */}
                  <Row className="w-100 mt-4">
                    <Col>
                      <h5 className="mb-3">Tournament Features</h5>
                      <div className="row">
                        {(() => {
                          // Define icons visible only in Tournaments
                          const tournamentOnlyIcons = [LobbyIcon.TURBO, LobbyIcon.HYPER];

                          // Define icons visible in both Tournament & Cash
                          const bothTournamentAndCashIcons = [
                            LobbyIcon.DOUBLE_BOARD,
                            LobbyIcon.BOMB_POT,
                            LobbyIcon.DEEP_STACK,
                            LobbyIcon.ANTE,
                            LobbyIcon.REAL_NAME, // high stakes
                          ];

                          // Get all available tournament icons
                          const availableIcons = [...bothTournamentAndCashIcons, ...tournamentOnlyIcons];

                          return availableIcons.map((icon) => (
                            <div key={icon} className="col-md-4 col-sm-6 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`lobbyIcon-${icon}`}
                                  checked={selectedLobbyIcons.includes(icon)}
                                  onChange={() => handleLobbyIconToggle(icon)}
                                  disabled={viewOnly}
                                />
                                <label className="form-check-label" htmlFor={`lobbyIcon-${icon}`}>
                                  {LOBBY_ICON_LABELS[icon]}
                                </label>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col></Col>
                  </Row>
                </Col>
              </div>
              {formStatus.error && (
                <div className="text-danger mt-2" role="alert">
                  {formStatus.error}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose} disabled={formStatus.isLoading}>
              Cancel
            </Button>
            {!viewOnly && (
              <Button
                disabled={!isValid || !hasChanges || formStatus.isLoading}
                onClick={handleSubmit(onSubmit)}
                color="primary"
              >
                {formStatus.isLoading
                  ? selectedTemplate?.id
                    ? 'Updating...'
                    : 'Creating...'
                  : selectedTemplate?.id
                    ? 'Update Tournament Template'
                    : 'Create New Tournament Template'}
              </Button>
            )}
          </ModalFooter>
        </Form>
      </Modal>
    </FormProvider>
  );
};

export default TournamentTemplateModalForm;
